import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, Building2, ArrowLeft, CheckCircle2, Send, Users, LogIn, Clock } from 'lucide-react';
import { useAuth, API_URL } from '../contexts/AuthContext';

const inputCls =
  'w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-800 text-[15px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0E7490] focus:border-transparent transition-all';

const Shell = ({ children }) => (
  <div
    className="min-h-screen w-full flex items-center justify-center p-4"
    style={{ background: 'linear-gradient(160deg, #ecf6fb 0%, #d8edf7 45%, #cfe8f5 100%)' }}
    data-testid="demo-login-page"
  >
    {children}
  </div>
);

export default function DemoLoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState('login'); // login | request
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ company_name: '', contact_name: '', email: '', phone: '' });
  const [requested, setRequested] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email.trim(), password);
      if (res.success) {
        toast.success('Welcome to your trial workspace!');
        navigate('/dashboard');
      } else {
        toast.error(res.error || 'Invalid email or password');
      }
    } catch {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/trial/request`, form);
      setRequested(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const Brand = () => (
    <div className="flex flex-col items-center text-center">
      <div className="h-14 w-14 rounded-2xl bg-[#0E7490] flex items-center justify-center shadow-lg shadow-[#0E7490]/30">
        <Users className="h-7 w-7 text-white" />
      </div>
      <h1 className="mt-3 text-xl font-extrabold text-slate-900">Employee Attendance Tracker</h1>
      <p className="text-[13px] font-semibold tracking-wide text-[#0E7490]">Trial Workspace Portal</p>
    </div>
  );

  if (requested) {
    return (
      <Shell>
        <div className="w-full max-w-sm">
          <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_18px_50px_-20px_rgba(8,47,73,0.35)] p-7 text-center fade-in" data-testid="demo-request-success">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="mt-4 text-lg font-bold text-slate-800">Request Received</h2>
            <p className="text-sm text-slate-500 mt-2">
              Thanks! Our team will review your request and email your <span className="font-semibold">owner login</span> (email &amp; password) shortly.
            </p>
            <button
              onClick={() => { setRequested(false); setMode('login'); }}
              data-testid="demo-back-to-login"
              className="mt-6 mx-auto flex items-center gap-1.5 text-sm font-semibold text-[#0E7490] hover:text-[#155E75]"
            >
              <ArrowLeft size={15} /> Back to Login
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="w-full max-w-sm">
        <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_18px_50px_-20px_rgba(8,47,73,0.35)] p-6 sm:p-7 fade-in">
          <Brand />

          <div className="mt-5 grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100">
            <button
              type="button"
              data-testid="demo-tab-login"
              onClick={() => setMode('login')}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-bold transition-colors ${mode === 'login' ? 'text-white bg-[#0E7490] shadow-sm' : 'text-slate-600 hover:bg-white'}`}
            >
              <LogIn size={16} /> LOGIN
            </button>
            <button
              type="button"
              data-testid="demo-tab-request"
              onClick={() => setMode('request')}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-bold transition-colors ${mode === 'request' ? 'text-white bg-[#0E7490] shadow-sm' : 'text-slate-600 hover:bg-white'}`}
            >
              <Send size={16} /> REQUEST DEMO
            </button>
          </div>

          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="mt-5 space-y-4">
              <p className="text-xs text-slate-500 text-center -mt-1">Owners &amp; staff sign in with the credentials shared on approval.</p>
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                  <Mail size={15} className="text-[#0E7490]" /> Email Address
                </label>
                <input data-testid="demo-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inputCls} />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                  <Lock size={15} className="text-[#0E7490]" /> Password
                </label>
                <div className="relative">
                  <input data-testid="demo-password-input" type={showPw ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className={inputCls + ' pr-11'} />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0E7490]">
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <button type="submit" data-testid="demo-login-submit" disabled={loading} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white bg-[#0E7490] hover:bg-[#155E75] shadow-md shadow-[#0E7490]/25 transition-all active:scale-[0.99] disabled:opacity-60">
                <LogIn size={18} /> {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
                <Clock size={13} /> Trials are valid for 7 days
              </p>
            </form>
          ) : (
            <form onSubmit={handleRequest} className="mt-5 space-y-3">
              <div>
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-1">
                  <Building2 size={14} className="text-[#0E7490]" /> Company Name
                </label>
                <input data-testid="demo-req-company" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Acme Pvt Ltd" className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Your Name</label>
                <input data-testid="demo-req-name" required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="John Doe" className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Work Email</label>
                <input data-testid="demo-req-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Phone (optional)</label>
                <input data-testid="demo-req-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 90000 00000" className={inputCls} />
              </div>
              <button type="submit" data-testid="demo-request-submit" disabled={loading} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white bg-[#0E7490] hover:bg-[#155E75] shadow-md shadow-[#0E7490]/25 transition-all active:scale-[0.99] disabled:opacity-60">
                <Send size={16} /> {loading ? 'Submitting...' : 'Request 7-Day Demo'}
              </button>
            </form>
          )}

          <button onClick={() => navigate('/')} data-testid="demo-to-main-login" className="mt-5 mx-auto flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-[#0E7490]">
            <ArrowLeft size={15} /> Back to main login
          </button>
        </div>
      </div>
    </Shell>
  );
}
